/**
 * One-shot Telegram initData exchange on mount.
 *
 * Lifecycle:
 *   1. Read raw initData via `@telegram-apps/sdk-react` (`useRawInitData`).
 *      The SDK reads it from `window.Telegram.WebApp.initData`, the launch
 *      URL hash, or session storage — whichever is available.
 *   2. POST it to `/api/v1/auth/telegram`. Backend returns a JWT + user.
 *   3. Persist JWT to sessionStorage (handled by `auth.exchangeInitData`).
 *   4. Register a re-auth callback with the API client so a 401 triggers a
 *      single fresh exchange + retry of the failed request.
 *
 * When initData is missing — i.e. the page was opened outside Telegram — we
 * surface `error = "no-init-data"` and the app shows an "Open from Telegram"
 * screen. We never silently log out or fall back to mock identities.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { retrieveRawInitData, useRawInitData } from "@telegram-apps/sdk-react";
import { exchangeInitData } from "../api/auth";
import { clearJwt, setReAuthenticator } from "../api/client";
import type { AuthUser } from "../lib/types";

/**
 * How long to wait for the SDK signal to populate before falling back
 * to the synchronous `retrieveRawInitData()` getter.
 *
 * In a "menu button" launch the signal fires before React even mounts,
 * so the wait is instantaneous. In a "reply-keyboard" launch on
 * Telegram Desktop the SDK sometimes never ticks the signal even
 * though `window.Telegram.WebApp.initData` is populated synchronously —
 * we work around that race by retrieving directly after this timeout.
 */
const INIT_DATA_FALLBACK_MS = 350;

export type AuthError =
  | { kind: "no-init-data" }
  | { kind: "network"; message: string }
  | { kind: "rejected"; message: string };

export interface UseAuthResult {
  user: AuthUser | null;
  isReady: boolean;
  error: AuthError | null;
}

export function useAuth(): UseAuthResult {
  const initDataFromSignal = useRawInitData();
  // Local override that mirrors the signal but also gets populated by
  // the post-timeout fallback path below (see `useEffect` further down).
  const [initDataOverride, setInitDataOverride] = useState<string | undefined>(
    undefined,
  );
  const initDataRaw = initDataFromSignal ?? initDataOverride;
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  // Guard against the React 19 StrictMode double-invoke in development.
  const inFlight = useRef<Promise<void> | null>(null);

  // Fallback path: if the SDK signal hasn't given us initData by the
  // timeout, query the synchronous getter ourselves. Covers the
  // reply-keyboard launch race on Telegram Desktop where the signal
  // never fires even though `window.Telegram.WebApp.initData` is set.
  useEffect(() => {
    if (initDataFromSignal !== undefined) {
      return; // signal already resolved — fallback not needed
    }
    const timer = window.setTimeout(() => {
      try {
        const raw = retrieveRawInitData();
        if (raw) {
          setInitDataOverride(raw);
        } else {
          // Tell the auth effect below to surface "no-init-data" instead
          // of spinning forever.
          setInitDataOverride("");
        }
      } catch {
        setInitDataOverride("");
      }
    }, INIT_DATA_FALLBACK_MS);
    return () => window.clearTimeout(timer);
  }, [initDataFromSignal]);

  const performExchange = useCallback(
    async (raw: string): Promise<void> => {
      try {
        const result = await exchangeInitData(raw);
        setUser(result.user);
        setError(null);
      } catch (cause) {
        clearJwt();
        const message = cause instanceof Error ? cause.message : "Unknown error";
        // Distinguish a network failure (fetch threw) from a backend rejection.
        const kind: AuthError["kind"] =
          cause instanceof TypeError ? "network" : "rejected";
        setError({ kind, message } as AuthError);
        throw cause;
      }
    },
    [],
  );

  useEffect(() => {
    if (initDataRaw === undefined) {
      // SDK hasn't resolved yet (rare with this hook; treat as still loading).
      return;
    }
    if (!initDataRaw) {
      setError({ kind: "no-init-data" });
      setIsReady(true);
      return;
    }
    if (inFlight.current) {
      return;
    }
    inFlight.current = performExchange(initDataRaw)
      .catch(() => {
        // Error state already set inside performExchange.
      })
      .finally(() => {
        setIsReady(true);
        inFlight.current = null;
      });
  }, [initDataRaw, performExchange]);

  // Register a re-auth callback with the API client so a 401 from any endpoint
  // can transparently refresh the JWT and retry once.
  useEffect(() => {
    if (!initDataRaw) {
      setReAuthenticator(null);
      return;
    }
    setReAuthenticator(() => performExchange(initDataRaw));
    return () => {
      setReAuthenticator(null);
    };
  }, [initDataRaw, performExchange]);

  return { user, isReady, error };
}
